import { BillingHelper } from "@/Billing/Helpers/BillingHelper";
import { BillingStore } from "@/Billing/Stores/BillingStore";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { BillingPaymentMethod } from "@/Common/Models/UI/BillingPaymentMethod";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";

class TransactionsSettingsContainerHandler extends BaseOperationHandler {
    private readonly helper: MyleHelper;
    private payableMethodId = BillingPaymentMethod.Payable.toString();

    constructor() {
        super();

        this.helper = new MyleHelper();

        this.RegisterOperation(LocalOperations.Billing.Transactions_SearchSettings_ShowOptionsButtonClick, this.DisplayMyNotesSettings);
        this.RegisterOperation(LocalOperations.Billing.Transactions_OnSettingsContainerCheckboxChange, this.OnSettingsContainerCheckboxChange);
        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.CloseSearchFilterPopup, false);
        this.RegisterOperation(LocalOperations.Billing.Transactions_SearchSettings_ProviderFilterTextChanged, this.ProviderFilterChanged);
        this.RegisterOperation(LocalOperations.Billing.Transactions_SearchSettings_BilledByFilterTextChanged, this.BilledByFilterChanged);
        this.RegisterOperation(LocalOperations.Billing.Transactions_SearchSettings_FromDateChanged, this.FromDateChanged);
        this.RegisterOperation(LocalOperations.Billing.Transactions_SearchSettings_ToDateChanged, this.ToDateChanged);
        this.RegisterOperation(LocalOperations.Billing.Transactions_SearchSettings_OkClick, this.OnOkClick);
        this.RegisterOperation(LocalOperations.Billing.Transactions_SearchSettings_CancelClick, this.OnCancelClick);

        this.RegisterOperation(LocalOperations.Billing.Transactions_SearchSettings_ColumnOrderingPopup_Open, this.ShowColumnOrderingPopup);
        this.RegisterOperation(LocalOperations.Billing.Transactions_SearchSettings_ColumnOrderingPopup_Close, this.CloseColumnOrderingPopup);

        this.RegisterOperation(LocalOperations.Billing.Transactions_SearchSettings_VisitFromDateChanged, this.VisitFromDateChanged);
        this.RegisterOperation(LocalOperations.Billing.Transactions_SearchSettings_VisitToDateChanged, this.VisitToDateChanged);
        this.RegisterOperation(LocalOperations.Billing.Transactions_SearchSettings_PaymentDateFromChanged, this.PaymentDateFromChanged);
        this.RegisterOperation(LocalOperations.Billing.Transactions_SearchSettings_PaymentDateToChanged, this.PaymentDateToChanged);
        this.RegisterOperation(LocalOperations.Billing.Transactions_SearchSettings_UpdatePayablePaymentMethodName, this.UpdatePayablePaymentMethodName);
    }

    private ShowColumnOrderingPopup(request: LocalEvent, store: BillingStore): void {
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("ColumnOrderingPopupOpen", true);
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("ShowTransactionsDisplaySettings", false);
        sessionStore.showGlobalOverlay(true);
    }

    private CloseColumnOrderingPopup(request: LocalEvent, store: BillingStore): void {
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("ColumnOrderingPopupOpen", false);
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("ShowTransactionsDisplaySettings", false);
        sessionStore.hideGlobalOverlay();
    }

    private FromDateChanged(request: LocalEvent, store: BillingStore): void {
        const nextFromDate = request.Get<Date>("value");
        const model = store.TransactionsInboxModel;
        store.TransactionsInboxModel = store.TransactionsInboxModel
            .set("DateFrom", nextFromDate)
            .set("ValidationError", ValidationHelper.validateSearchDateRangeMax5Years(nextFromDate, model.get("DateTo")));
    }

    private ToDateChanged(request: LocalEvent, store: BillingStore): void {
        const nextToDate = request.Get<Date>("value");
        const model = store.TransactionsInboxModel;
        store.TransactionsInboxModel = store.TransactionsInboxModel
            .set("DateTo", nextToDate)
            .set("ValidationError", ValidationHelper.validateSearchDateRangeMax5Years(model.get("DateFrom"), nextToDate));
    }

    private OnSettingsContainerCheckboxChange(request: LocalEvent, store: BillingStore): void {
        var target = request.Get<CheckboxTarget>("target");
        var checked = request.Get<boolean>("checked");
        var id = request.Get<string>("id");

        switch (target) {
            case CheckboxTarget.Myle_Billing_Transactions_Sites:

                var uncheckedBilledBy = store.helper.ChangeCheckStateFromAllICheckable(store.TransactionsInboxModel.get("BilledByGlobalCache"), false);
                var uncheckedProvider = store.helper.ChangeCheckStateFromAllICheckable(store.TransactionsInboxModel.get("ProviderGlobalCache"), false);
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("BilledByGlobalCache", uncheckedBilledBy);
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("ProviderGlobalCache", uncheckedProvider);
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("SelectAllProviderChecked", false);
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("SelectAllBilledByChecked", false);
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("Sites", store.helper.FindUpdateICheckableItem<SiteUIModel>(store.TransactionsInboxModel.get("Sites"), id, checked));
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("ProviderFiltered", store.getTransactionsProviderBasedOnSitesAndQuery());
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("BilledByFiltered", store.getTransactionsBilledByBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_Billing_Transactions_Provider:
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("ProviderGlobalCache", this.helper.FindUpdateICheckableItem<EmployeeUIModel>(store.TransactionsInboxModel.get("ProviderGlobalCache"), id, checked));
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("ProviderFiltered", store.getTransactionsProviderBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_Billing_Transactions_BilledBy:
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("BilledByGlobalCache", this.helper.FindUpdateICheckableItem<EmployeeUIModel>(store.TransactionsInboxModel.get("BilledByGlobalCache"), id, checked));
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("BilledByFiltered", store.getTransactionsBilledByBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_Billing_Transactions_SitesControlCheck:
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("SelectAllSitesChecked", checked);
                var sites = store.helper.ChangeCheckStateFromAllICheckable(store.TransactionsInboxModel.get("Sites"), checked);
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("Sites", sites);
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("ProviderFiltered", store.getTransactionsProviderBasedOnSitesAndQuery());
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("BilledByFiltered", store.getTransactionsBilledByBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_Billing_Transactions_ProviderControlCheck:
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("SelectAllProviderChecked", checked);
                var list = store.getTransactionsProviderBasedOnSitesAndQuery();
                var listOfClinicianIds = list.toArray().map(i => i.Id);
                var c = store.helper.FindUpdateICheckableItems(store.TransactionsInboxModel.get("ProviderGlobalCache"), listOfClinicianIds, checked);
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("ProviderGlobalCache", c);
                var checkedList = store.getTransactionsProviderBasedOnSitesAndQuery();
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("ProviderFiltered", checkedList);
                break;

            case CheckboxTarget.Myle_Billing_Transactions_BilledByControlCheck:
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("SelectAllBilledByChecked", checked);
                var list = store.getTransactionsBilledByBasedOnSitesAndQuery();
                var listOfClinicianIds = list.toArray().map(i => i.Id);
                var c = store.helper.FindUpdateICheckableItems(store.TransactionsInboxModel.get("BilledByGlobalCache"), listOfClinicianIds, checked);
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("BilledByGlobalCache", c);
                var checkedList = store.getTransactionsBilledByBasedOnSitesAndQuery();
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("BilledByFiltered", checkedList);
                break;

            case CheckboxTarget.Myle_Billing_Settings_ValidityAllChecked:
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("SelectAllValidityChecked", checked);
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("Validity", this.helper.ChangeCheckStateFromAllICheckable(store.TransactionsInboxModel.get("Validity"), checked));
                break;

            case CheckboxTarget.Myle_Billing_Settings_Validity:
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("Validity", this.helper.FindUpdateICheckableItem<CheckboxUIModel>(store.TransactionsInboxModel.get("Validity"), id, checked));

                break;

            case CheckboxTarget.Myle_Billing_Transactions_Status:
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("Status", this.helper.FindUpdateICheckableItem<CheckboxUIModel>(store.TransactionsInboxModel.get("Status"), id, checked));
                break;

            case CheckboxTarget.Myle_Billing_Transactions_StatusControlCheck:
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("SelectAllStatusChecked", checked);
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("Status", this.helper.ChangeCheckStateFromAllICheckable(store.TransactionsInboxModel.get("Status"), checked));
                break;

            case CheckboxTarget.Myle_Billing_Transactions_PaymentMethod:
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("PaymentMethod", store.helper.FindUpdateICheckableItem<CheckboxUIModel>(store.TransactionsInboxModel.get("PaymentMethod"), id, checked));
                this.updateOpenStatusVisibility(store);
                break;
            case CheckboxTarget.Myle_Billing_Transactions_PaymentsControlCheck:
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("SelectAllPaymentChecked", checked);
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("PaymentMethod", this.helper.ChangeCheckStateFromAllICheckable(store.TransactionsInboxModel.get("PaymentMethod"), checked));
                this.updateOpenStatusVisibility(store);
                break;

            case CheckboxTarget.Myle_Billing_Transactions_OnlySelectedSites:
                store.TransactionsInboxModel = store.TransactionsInboxModel.set("ShowOnlySelectedSites", checked);
                break;
        }
    }

    private updateOpenStatusVisibility(store: BillingStore): void {
        if (cacheStore.ClinicSettings.EnablePrivateBillRecipient) {
            const statusOpenId = "open_status";
            const checkedPaymentMethods = store.TransactionsInboxModel.get("PaymentMethod").filter(p => p.Checked);

            if (checkedPaymentMethods.size === 1 && checkedPaymentMethods?.first()?.Id === this.payableMethodId) {
                // hide the open status option
                store.TransactionsInboxModel.set("Status", store.TransactionsInboxModel.get("Status").map((i: CheckboxUIModel) => {
                    if (i.Id === statusOpenId) { i.Visible = false; i.Checked = false; }
                    return i;
                }).toList());
            }
            else {
                // show the open status option
                store.TransactionsInboxModel.set("Status", store.TransactionsInboxModel.get("Status").map((i: CheckboxUIModel) => {
                    if (i.Id === statusOpenId) { i.Visible = true; }
                    return i;
                }).toList());
            }
        }
    }

    private UpdatePayablePaymentMethodName(request: LocalEvent, store: BillingStore): void {
        store.TransactionsInboxModel.set("PaymentMethod", store.TransactionsInboxModel.get("PaymentMethod").map((i: CheckboxUIModel) => {
            if (i.Id === this.payableMethodId) { i.Name = cacheStore.GetString("PayableClosedOnlyLabel"); }
            return i;
        }).toList());
    }

    private DisplayMyNotesSettings(request: LocalEvent, store: BillingStore): void {
        var open = store.TransactionsInboxModel.get("ShowTransactionsDisplaySettings");
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("ShowTransactionsDisplaySettings", !open);
    }

    private OnCancelClick(request: LocalEvent, store: BillingStore): void {
        const model = store.TransactionsInboxModel = store.TransactionsInboxModel.set("ShowTransactionsDisplaySettings", false);

        const validationError = ValidationHelper.validateSearchDateRangeMax5Years(model.get("DateFrom"), model.get("DateTo"));

        if (validationError !== null) {
            store.TransactionsInboxModel = store.TransactionsInboxModel
                .set("DateFrom", BillingHelper.GetSearchDefaultFromDate())
                .set("DateTo", BillingHelper.GetSearchDefaultToDate())
                .set("ValidationError", null);
        }
    }

    private OnOkClick(request: LocalEvent, store: BillingStore): void {
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("ShowTransactionsDisplaySettings", false);

        store.BillingAction.LoadTransactionsData(store.TransactionsInboxModel);
    }

    private CloseSearchFilterPopup(request: LocalEvent, store: BillingStore): void {
        if (store.TransactionsInboxModel.get("ShowTransactionsDisplaySettings") === true) {
            this.OnOkClick(request, store);
            store.EmitChangeToListeners();
        }
    }

    private ProviderFilterChanged(request: LocalEvent, store: BillingStore): void {
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("ProviderSearchText", request.Get<string>("value"));
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("ProviderFiltered", store.getTransactionsProviderBasedOnSitesAndQuery());
    }

    private BilledByFilterChanged(request: LocalEvent, store: BillingStore): void {
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("BilledBySearchText", request.Get<string>("value"));
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("BilledByFiltered", store.getTransactionsBilledByBasedOnSitesAndQuery());
    }

    private VisitFromDateChanged(request: LocalEvent, store: BillingStore): void {
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("VisitDateFrom", request.Get<Date>("value"));
    }

    private VisitToDateChanged(request: LocalEvent, store: BillingStore): void {
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("VisitDateTo", request.Get<Date>("value"));
    }

    private PaymentDateFromChanged(request: LocalEvent, store: BillingStore): void {
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("PaymentDateFrom", request.Get<Date>("value"));
    }

    private PaymentDateToChanged(request: LocalEvent, store: BillingStore): void {
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("PaymentDateTo", request.Get<Date>("value"));
    }
}

export { TransactionsSettingsContainerHandler };
